<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          {{ submitting ? '儲存中...' : '儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3>整合狀態</h3>
          <div class="form-group">
            <div class="integration-switch-row">
              <div class="integration-switch-info">
                <span class="integration-switch-label">啟用 Wixtar 整合</span>
                <small class="form-text">停用後將不會進行任何同步操作</small>
                <small class="form-text">包含 Webhook 與 Wixtar API 呼叫（會員/訂單/票券/點數查詢與同步）都會停用。</small>
              </div>
              <label class="integration-toggle-switch">
                <input
                  v-model="form.isActive"
                  type="checkbox"
                />
                <span class="slider"></span>
              </label>
            </div>
            <small class="form-text integration-switch-status" :class="{ active: form.isActive }">
              目前狀態：{{ form.isActive ? '已啟用' : '已停用' }}
            </small>
          </div>
        </div>

        <div class="tabs-nav">
          <button
            type="button"
            :class="['tab-btn', { active: activeTab === 'api' }]"
            @click="activeTab = 'api'"
          >
            <i class="ri-settings-3-line"></i>
            API
          </button>
          <button
            type="button"
            :class="['tab-btn', { active: activeTab === 'webhook' }]"
            @click="activeTab = 'webhook'"
          >
            <i class="ri-webhook-line"></i>
            Webhook
          </button>
        </div>

        <div class="tabs-content">
          <div v-show="activeTab === 'api'" class="tab-pane">
            <div class="form-section">
              <h3>API 認證設定</h3>

              <div class="form-group">
                <label>API URL <span class="required">*</span></label>
                <input
                  v-model="form.apiUrl"
                  type="url"
                  class="form-control"
                  required
                  placeholder="https://omni.wixtar.com"
                />
                <small class="form-text">
                  Wixtar API 的基礎 URL
                  <br>
                  <strong>正式環境：</strong>https://omni.wixtar.com
                  <br>
                  <strong>測試環境：</strong>https://omni-dev.wixtar.com
                </small>
              </div>

              <div class="form-group">
                <label>API 預設版本 <span class="required">*</span></label>
                <select v-model="form.apiVersion" class="form-control" required>
                  <option value="v1">v1</option>
                  <option value="v2">v2</option>
                  <option value="v3">v3</option>
                </select>
                <small class="form-text">
                  選擇 Wixtar API 預設版本（僅用於沒有版本映射的端點）
                  <br>
                  <strong>注意：</strong>系統會自動為每個端點選擇最新支援的版本：
                  <br>
                  • 會員 API：自動使用 v2（最新版本）
                  <br>
                  • 訂單 API：自動使用 v2（最新版本）
                  <br>
                  • 產品 API：自動使用 v3（僅 v3 支援）
                  <br>
                  • 票券 API：自動使用 v3（最新版本）
                  <br>
                  • 如果請求失敗，系統會自動降級到支援的版本
                </small>
              </div>

              <div class="form-group">
                <label>Wixtar Client ID (authkey) <span class="required">*</span></label>
                <input
                  v-model="form.wixtarClientId"
                  type="text"
                  class="form-control"
                  required
                  placeholder="請輸入 Wixtar Client ID"
                />
                <small class="form-text">
                  對應 Wixtar API 的 <code>authkey</code> 參數（辨識公司的代碼）
                </small>
              </div>

              <div class="form-group">
                <label>Wixtar Client Secret (acckey) <span class="required">*</span></label>
                <div class="input-group">
                  <input
                    v-model="form.wixtarClientSecret"
                    :type="showWixtarClientSecret ? 'text' : 'password'"
                    class="form-control"
                    required
                    placeholder="請輸入 Wixtar Client Secret"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="showWixtarClientSecret = !showWixtarClientSecret"
                    :title="showWixtarClientSecret ? '隱藏' : '顯示'"
                  >
                    <i :class="showWixtarClientSecret ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
                <small class="form-text">
                  對應 Wixtar API 的 <code>acckey</code> 參數（會員登入後的代碼），用於取得 Access Token，系統會加密儲存
                </small>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'webhook'" class="tab-pane">
            <div class="form-section">
              <h3>Webhook 設定</h3>

              <!-- Webhook URLs 顯示 -->
              <div class="form-group">
                <label>Webhook URLs</label>
                <div class="webhook-urls">
                  <div class="webhook-url-item">
                    <div class="webhook-url-label">
                      <i class="ri-webhook-line"></i>
                      統一 Webhook Endpoint
                    </div>
                    <div class="input-group">
                      <input
                        :value="unifiedWebhookUrl"
                        type="text"
                        class="form-control"
                        readonly
                        @click="$event.target.select()"
                      />
                      <button
                        type="button"
                        class="btn btn-outline-secondary"
                        @click="copyToClipboard(unifiedWebhookUrl, 'Webhook Endpoint')"
                        title="複製"
                      >
                        <i class="ri-file-copy-line"></i>
                      </button>
                    </div>
                    <small class="form-text">Wixtar 所有事件統一送到此端點</small>
                  </div>
                </div>
                <div class="webhook-url-note">
                  <i class="ri-information-line"></i>
                  <span>請將以上 URL 設定到 Wixtar 後台的 Webhook 設定中</span>
                </div>
              </div>

              <div class="form-group">
                <label>Webhook Secret (webhook_acckey)</label>
                <div class="input-group">
                  <input
                    v-model="form.webhookSecret"
                    :type="showWebhookSecret ? 'text' : 'password'"
                    class="form-control"
                    placeholder="請輸入 Webhook Secret（選填）"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="showWebhookSecret = !showWebhookSecret"
                    :title="showWebhookSecret ? '隱藏' : '顯示'"
                  >
                    <i :class="showWebhookSecret ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
                <small class="form-text">
                  對應 Wixtar API 的 <code>webhook_acckey</code> 參數（webhook 代碼），用於驗證 Webhook 請求的簽章，系統會加密儲存
                </small>
                <small v-if="!hasWebhookSecret" class="form-text text-warning">
                  <i class="ri-alert-line"></i>
                  未設定 Webhook Secret 時，將無法使用訂閱事件和自動同步功能
                </small>
              </div>

              <div class="form-group">
                <label>訂閱事件</label>
                <div class="event-actions">
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    :disabled="!hasWebhookSecret"
                    @click="selectAllWebhookEvents"
                  >
                    全選
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    :disabled="!hasWebhookSecret"
                    @click="clearAllWebhookEvents"
                  >
                    全不選
                  </button>
                </div>
                <div v-if="!hasWebhookSecret" class="disabled-notice">
                  <i class="ri-lock-line"></i>
                  請先設定 Webhook Secret 才能啟用訂閱事件
                </div>
                <div class="checkbox-group">
                  <label :class="{ disabled: !hasWebhookSecret }">
                    <input
                      v-model="form.webhookEvents"
                      type="checkbox"
                      value="member.created"
                      :disabled="!hasWebhookSecret"
                    />
                    會員建立（member.created / user.add）
                  </label>
                  <label :class="{ disabled: !hasWebhookSecret }">
                    <input
                      v-model="form.webhookEvents"
                      type="checkbox"
                      value="member.updated"
                      :disabled="!hasWebhookSecret"
                    />
                    會員更新（member.updated / user.update）
                  </label>
                  <label :class="{ disabled: !hasWebhookSecret }">
                    <input
                      v-model="form.webhookEvents"
                      type="checkbox"
                      value="order.created"
                      :disabled="!hasWebhookSecret"
                    />
                    訂單建立（order.created / order.create）
                  </label>
                  <label :class="{ disabled: !hasWebhookSecret }">
                    <input
                      v-model="form.webhookEvents"
                      type="checkbox"
                      value="order.updated"
                      :disabled="!hasWebhookSecret"
                    />
                    訂單更新（order.updated / order.change，含部分退款、缺貨）
                  </label>
                  <label :class="{ disabled: !hasWebhookSecret }">
                    <input
                      v-model="form.webhookEvents"
                      type="checkbox"
                      value="order.cancelled"
                      :disabled="!hasWebhookSecret"
                    />
                    訂單取消（order.cancelled / order.cancel）
                  </label>
                  <label :class="{ disabled: !hasWebhookSecret }">
                    <input
                      v-model="form.webhookEvents"
                      type="checkbox"
                      value="point.change"
                      :disabled="!hasWebhookSecret"
                    />
                    點數異動（point.change，名稱含 point 的事件都算）
                  </label>
                  <label :class="{ disabled: !hasWebhookSecret }">
                    <input
                      v-model="form.webhookEvents"
                      type="checkbox"
                      value="store.change"
                      :disabled="!hasWebhookSecret"
                    />
                    門市異動（store.change）
                  </label>
                  <label :class="{ disabled: !hasWebhookSecret }">
                    <input
                      v-model="form.webhookEvents"
                      type="checkbox"
                      value="ticket.complete"
                      :disabled="!hasWebhookSecret"
                    />
                    票券派發完成（ticket.complete）
                  </label>
                  <label :class="{ disabled: !hasWebhookSecret }">
                    <input
                      v-model="form.webhookEvents"
                      type="checkbox"
                      value="ticket.change_status"
                      :disabled="!hasWebhookSecret"
                    />
                    票券狀態異動（ticket.change_status）
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'WixtarConfigEdit',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('Wixtar 整合設定');

    const loading = ref(false);
    const submitting = ref(false);
    const showWixtarClientSecret = ref(false);
    const showWebhookSecret = ref(false);
    const activeTab = ref('api');
    
    const form = reactive({
      apiUrl: 'https://omni.wixtar.com', // 預設 Wixtar API base URL（正式環境）
      apiVersion: 'v2', // 預設 API 版本（用於沒有版本映射的端點）
      wixtarClientId: '',
      wixtarClientSecret: '',
      webhookSecret: '',
      webhookEvents: [], // 預設不勾選，需要設定 webhook secret 才能使用
      isActive: true
    });

    // Computed: 是否已設定 Webhook Secret
    const hasWebhookSecret = computed(() => {
      return form.webhookSecret && form.webhookSecret.trim().length > 0;
    });

    const allWebhookEventValues = [
      'member.created',
      'member.updated',
      'order.created',
      'order.updated',
      'order.cancelled',
      'point.change',
      'store.change',
      'ticket.complete',
      'ticket.change_status'
    ];

    // Computed: 統一 Webhook URL
    const unifiedWebhookUrl = computed(() => {
      const rawBaseUrl = process.env.VUE_APP_API_BASE_URL || window.location.origin.replace(':8080', ':3000');
      const baseUrlWithOrigin = String(rawBaseUrl).startsWith('/')
        ? `${window.location.origin.replace(':8080', ':3000')}${rawBaseUrl}`
        : String(rawBaseUrl);
      const normalizedBaseUrl = baseUrlWithOrigin.replace(/\/api\/v1\/?$/, '');
      return `${normalizedBaseUrl}/api/v1/integrations/wixtar/webhook`;
    });

    // 複製到剪貼簿
    const copyToClipboard = async (text, label) => {
      try {
        await navigator.clipboard.writeText(text);
        await showSuccess('複製成功', `${label} 已複製到剪貼簿`);
      } catch (err) {
        console.error('複製失敗:', err);
        await showError('複製失敗', '無法複製到剪貼簿');
      }
    };

    const selectAllWebhookEvents = () => {
      if (!hasWebhookSecret.value) {
        return;
      }
      form.webhookEvents = [...allWebhookEventValues];
    };

    const clearAllWebhookEvents = () => {
      if (!hasWebhookSecret.value) {
        return;
      }
      form.webhookEvents = [];
    };

    const fetchConfig = async () => {
      loading.value = true;
      try {
        // 使用 decrypt=true 獲取解密後的敏感資訊
        const response = await wixtarService.getConfig(true);
        console.log('[Wixtar Edit] 獲取配置回應:', response);
        
        if (response && response.success && response.data) {
          const config = response.data;
          console.log('[Wixtar Edit] 載入配置資料:', {
            apiUrl: config.apiUrl,
            apiVersion: config.apiVersion,
            wixtarClientId: config.wixtarClientId,
            wixtarClientSecret: config.wixtarClientSecret ? '***已載入***' : '未設定',
            webhookSecret: config.webhookSecret ? '***已載入***' : '未設定'
          });
          
          form.apiUrl = config.apiUrl || 'https://omni.wixtar.com';
          form.apiVersion = config.apiVersion || 'v2';
          form.wixtarClientId = config.wixtarClientId || '';
          // 顯示解密後的敏感資訊
          form.wixtarClientSecret = config.wixtarClientSecret || '';
          form.webhookSecret = config.webhookSecret || '';
          // 只有在已有 webhook secret 時才載入事件設定，否則使用預設空陣列
          form.webhookEvents = (config.webhookSecret && config.webhookEvents) ? config.webhookEvents : [];
          form.isActive = config.isActive !== undefined ? config.isActive : true;
        } else {
          // 如果配置不存在，使用預設值
          console.log('[Wixtar Edit] 配置不存在，使用預設值');
          form.apiUrl = 'https://omni.wixtar.com';
          form.apiVersion = 'v2';
        }
      } catch (err) {
        // 如果是 404，表示尚未設定，使用預設值
        if (err.response?.status === 404) {
          console.log('[Wixtar Edit] 配置未設定（404），使用預設值');
          form.apiUrl = 'https://omni.wixtar.com';
          form.apiVersion = 'v2';
        } else {
          console.error('[Wixtar Edit] 獲取配置失敗:', err);
          await showError('載入失敗', err.message || '獲取配置失敗');
        }
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      // 驗證必填欄位
      if (!form.apiUrl || !form.wixtarClientId || !form.wixtarClientSecret) {
        await showError('驗證失敗', '請填寫所有必填欄位');
        return;
      }

      submitting.value = true;
      try {
        console.log('[Wixtar Edit] 開始儲存設定，表單資料:', {
          apiUrl: form.apiUrl,
          apiVersion: form.apiVersion,
          wixtarClientId: form.wixtarClientId,
          wixtarClientSecret: form.wixtarClientSecret ? '***已填寫***' : '未填寫',
          webhookSecret: form.webhookSecret ? '***已填寫***' : '未填寫',
          webhookEvents: form.webhookEvents,
          isActive: form.isActive
        });

        const response = await wixtarService.setConfig(form);
        
        console.log('[Wixtar Edit] API 回應:', response);
        console.log('[Wixtar Edit] 回應類型:', typeof response);
        console.log('[Wixtar Edit] 回應 keys:', response ? Object.keys(response) : 'null');
        console.log('[Wixtar Edit] response.success:', response?.success);
        
        // 處理不同的回應格式
        // 情況1：標準格式 { success: true, data: {...}, message: '...' }
        if (response && response.success === true) {
          console.log('[Wixtar Edit] 儲存成功（標準格式）');
          await showSuccess('儲存成功', 'Wixtar 整合設定已儲存');
          router.push({ name: 'ClientSystemSettings', query: { tab: 'wixtar' } });
        } 
        // 情況2：直接返回資料物件（可能是 axios interceptor 的問題）
        else if (response && response.id && response.platformId && response.clientId) {
          console.log('[Wixtar Edit] 儲存成功（直接資料格式）');
          await showSuccess('儲存成功', 'Wixtar 整合設定已儲存');
          router.push({ name: 'ClientSystemSettings', query: { tab: 'wixtar' } });
        }
        // 情況3：錯誤格式
        else {
          console.error('[Wixtar Edit] 儲存失敗 - 回應格式錯誤:', response);
          const errorMessage = response?.message || response?.error?.message || '儲存設定失敗';
          await showError('儲存失敗', errorMessage);
        }
      } catch (err) {
        console.error('[Wixtar Edit] 儲存設定失敗 - 例外錯誤:', err);
        console.error('[Wixtar Edit] 錯誤詳情:', {
          message: err.message,
          response: err.response,
          responseData: err.response?.data,
          status: err.response?.status,
          statusText: err.response?.statusText
        });
        
        let errorMessage = '儲存設定失敗';
        if (err.response?.data) {
          const errorData = err.response.data;
          errorMessage = errorData.message || errorData.error?.message || errorMessage;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        await showError('儲存失敗', errorMessage);
      } finally {
        submitting.value = false;
      }
    };

    const handleBack = () => {
      router.push({ name: 'ClientSystemSettings', query: { tab: 'wixtar' } });
    };

    onMounted(() => {
      fetchConfig();
    });

    return {
      pageTitle,
      loading,
      submitting,
      showWixtarClientSecret,
      showWebhookSecret,
      activeTab,
      form,
      hasWebhookSecret,
      unifiedWebhookUrl,
      copyToClipboard,
      selectAllWebhookEvents,
      clearAllWebhookEvents,
      handleSubmit,
      handleBack
    };
  }
};
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.page-outer > .page-container {
  /* 預留固定頂部操作列高度，避免內容被覆蓋 */
  padding-top: 88px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 24px;
}

.form-section {
  margin-bottom: 32px;
}

.form-section h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.form-group {
  margin-bottom: 20px;
}

.integration-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.integration-switch-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.integration-switch-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.integration-toggle-switch {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 30px;
  flex-shrink: 0;
}

.integration-toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.integration-toggle-switch .slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #cbd5e1;
  border-radius: 999px;
  transition: background-color 0.2s ease;
}

.integration-toggle-switch .slider::before {
  position: absolute;
  content: '';
  height: 22px;
  width: 22px;
  left: 4px;
  top: 4px;
  background-color: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.integration-toggle-switch input:checked + .slider {
  background-color: #16a34a;
}

.integration-toggle-switch input:checked + .slider::before {
  transform: translateX(22px);
}

.integration-switch-status {
  margin-top: 8px;
  color: #64748b;
}

.integration-switch-status.active {
  color: #166534;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
}

.required {
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.input-group {
  display: flex;
  gap: 0;
}

.input-group .form-control {
  flex: 1;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.input-group .btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
}

.btn-outline-secondary {
  background-color: #fff;
  color: #6c757d;
  border: 1px solid #ced4da;
  border-left: none;
}

.btn-outline-secondary:hover {
  background-color: #f8f9fa;
  color: #495057;
}

.btn-outline-secondary i {
  font-size: 18px;
}

.webhook-urls {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 12px;
}

.webhook-url-item {
  padding: 16px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.webhook-url-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
}

.webhook-url-label i {
  font-size: 18px;
  color: #007bff;
}

.webhook-url-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 13px;
  margin-top: 12px;
}

.webhook-url-note i {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 2px;
}

.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: normal;
  cursor: pointer;
}

.checkbox-group input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-group label.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.checkbox-group label.disabled input[type="checkbox"] {
  cursor: not-allowed;
}

.disabled-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 13px;
  margin-bottom: 12px;
}

.disabled-notice i {
  font-size: 16px;
}

.text-warning {
  color: #856404 !important;
  background: #fff3cd;
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.text-warning i {
  font-size: 16px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #007bff;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.loading-state {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .page-outer > .page-container {
    padding-top: 104px;
  }

  .integration-switch-row {
    align-items: flex-start;
  }
}
</style>
